import { Group, Menu, Text, UnstyledButton } from '@mantine/core';
import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import cx from 'clsx';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import { IconChevronDown, IconReportAnalytics } from '@tabler/icons-react';
import { useQuery } from '@apollo/client';

import { getReportsList } from '@/api';
import TablerIconDynamic from '@/components/TablerIconDynamic';
import classes from './private-layout.module.css';
import { getReportLink } from './private-layout.helpers';
import MenuLink from './MenuLink';
import { Page, usePermissions } from '@/permissions';

// button width + gap
const MORE_WIDTH_GAP = 105 + 10;

const PrivateLayoutReports: React.FC = () => {
  const [isOpen, { open, close }] = useDisclosure(false);
  const { data } = useQuery(getReportsList);
  const { canAccess } = usePermissions();
  const reports = data?.getReportsConfigurations?.reportsConfigurations ?? [];

  const { ref: containerRef, width } = useElementSize();
  const measureRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (!measureRef.current || reports.length === 0) {
      return;
    }

    const top = itemRefs.current[0]?.offsetTop ?? 0;
    let count = 0;

    for (const el of itemRefs.current) {
      if (!el) {
        continue;
      }
      if (el.offsetTop > top || el.offsetWidth >= measureRef.current.offsetWidth) {
        break;
      }
      count++;
    }

    setVisibleCount(count);
  }, [width, reports]);

  const visibleItems = reports.slice(0, visibleCount);
  const hiddenItems = reports.slice(visibleCount);

  if (!canAccess(Page.Reports)) {
    return null;
  }

  return (
    <>
      <Group
        visibleFrom="sm"
        align="flex-start"
        gap="xs"
        ref={containerRef}
        wrap="nowrap"
        style={{ flexGrow: 1 }}
      >
        {visibleItems.map((item) => (
          <MenuLink
            key={item.menuTitle}
            to={getReportLink(item)}
            icon={item.iconName}
            title={item.menuTitle}
          />
        ))}
        {hiddenItems.length ? (
          <Menu
            withArrow
            position="bottom-end"
            transitionProps={{ transition: 'pop-top-right' }}
            onClose={close}
            onOpen={open}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton className={cx(classes.targetButton, classes.more, { [classes.moreActive]: isOpen })}>
                <Group gap={7}>
                  <IconReportAnalytics size="1.5rem" />
                  <Text fw={500} size="sm" lh={1} mr={3}>
                    More
                  </Text>
                  <IconChevronDown size={12} stroke={1.5} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {hiddenItems.map((item) => (
                <Menu.Item component={NavLink} to={getReportLink(item)} key={item.menuTitle}>
                  <Group gap="xs">
                    <TablerIconDynamic size="1.5rem" icon={item.iconName} />
                    <Text span>{item.menuTitle}</Text>
                  </Group>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </Group>
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          width: width - MORE_WIDTH_GAP,
        }}
      >
        <Group gap="xs" wrap="wrap">
          {reports.map((item, index) => (
            <NavLink
              key={item.menuTitle}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              className={classes.link}
              to={getReportLink(item)}
            >
              <TablerIconDynamic size="1.5rem" icon={item.iconName} />
              <Text span>{item.menuTitle}</Text>
            </NavLink>
          ))}
        </Group>
      </div>
    </>
  );
};

export default PrivateLayoutReports;
